import React from "react"
import { msalInstance } from "@/config/msalInstance"
import { FallbackErrorComponent } from "@/components/Fallback/FallbackErrorComponent"
import { appInsights } from "@/telemetry/appInsights"
import { getValidAccessToken } from "@/utils/accessToken"
import { forceLogoutAndReauthenticate } from "@/utils/authSessionRecovery"
import { isChunkLoadError, reloadForStaleAssets } from "@/utils/chunkLoadError"
import { isEffectCleanupTypeError } from "@/utils/effectCleanupError"

interface ErrorBoundaryProps {
    children: React.ReactNode
}

interface ErrorBoundaryState {
    hasError: boolean
    isRefreshing: boolean
    isRecoverableEffectCleanupError: boolean
    recoverableErrorMessage: string | null
}

const MAX_RETRIES = 10
const RETRY_TIMEFRAME = 30 * 60 * 1000 // 30 minutes

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = {
        hasError: false,
        isRefreshing: false,
        isRecoverableEffectCleanupError: false,
        recoverableErrorMessage: null,
    }

    refreshTimeout: ReturnType<typeof setTimeout> | null = null

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        if (isEffectCleanupTypeError(error)) {
            return {
                hasError: true,
                isRecoverableEffectCleanupError: true,
                recoverableErrorMessage: error.message,
            }
        }

        return { hasError: true, isRecoverableEffectCleanupError: false, recoverableErrorMessage: null }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error("Error caught by ErrorBoundary:", error, errorInfo)

        if (isChunkLoadError(error)) {
            reloadForStaleAssets()
            return
        }

        if (isEffectCleanupTypeError(error)) {
            this.trackRecoverableEffectCleanupError(error, errorInfo)
            return
        }

        appInsights.trackException({ exception: error, properties: errorInfo })

        const now = new Date().getTime()
        const lastRetryTime = parseInt(sessionStorage.getItem("lastRetryTime") ?? "0", 10)
        const retryCount = parseInt(sessionStorage.getItem("retryCount") ?? "0", 10)

        if (now - lastRetryTime < RETRY_TIMEFRAME) {
            if (retryCount < MAX_RETRIES) {
                sessionStorage.setItem("retryCount", (retryCount + 1).toString())
                sessionStorage.setItem("lastRetryTime", now.toString())

                if (this.refreshTimeout) {
                    clearTimeout(this.refreshTimeout)
                }
                this.refreshTimeout = setTimeout(this.handleAuthRefresh, retryCount * retryCount * 1000)
            } else {
                console.log("Max retries reached within the timeframe. Not handling error.")
                appInsights.trackEvent({ name: "ErrorBoundary_MaxRetriesReached" })
            }
        } else {
            sessionStorage.setItem("retryCount", "1")
            sessionStorage.setItem("lastRetryTime", now.toString())
            this.refreshTimeout = setTimeout(this.handleAuthRefresh, 1000)
        }
    }

    componentDidUpdate(_prevProps: ErrorBoundaryProps, prevState: ErrorBoundaryState) {
        if (
            this.state.isRecoverableEffectCleanupError &&
            !prevState.isRecoverableEffectCleanupError &&
            this.state.recoverableErrorMessage
        ) {
            this.trackRecoverableEffectCleanupError(new TypeError(this.state.recoverableErrorMessage))
        }
    }

    trackRecoverableEffectCleanupError(error: Error, errorInfo?: React.ErrorInfo) {
        appInsights.trackException({
            exception: error,
            properties: { ...(errorInfo ?? {}), context: "EffectCleanupTypeError" },
        })
        this.setState({
            hasError: false,
            isRecoverableEffectCleanupError: false,
            recoverableErrorMessage: null,
        })
    }

    componentWillUnmount() {
        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout)
        }
    }

    handleAuthRefresh = async () => {
        if (msalInstance.getAllAccounts().length === 0) {
            appInsights.trackEvent({ name: "ErrorBoundary_NoAccounts" })
            const retryCount = parseInt(sessionStorage.getItem("retryCount") ?? "0", 10)
            if (retryCount >= MAX_RETRIES) {
                this.setState({ isRefreshing: false })
            }
            return
        }

        try {
            const token = await getValidAccessToken({ forceRefresh: true })
            if (token) {
                sessionStorage.setItem("retryCount", "0")
                sessionStorage.setItem("lastRetryTime", "0")
                this.setState({ hasError: false, isRefreshing: false })
                appInsights.trackEvent({ name: "ErrorBoundary_AuthRefreshSuccess" })
                return
            }

            await forceLogoutAndReauthenticate()
        } catch (authError) {
            console.error("Auth refresh or login redirect failed:", authError)
            appInsights.trackException({
                exception: authError as Error,
                properties: { context: "ErrorBoundary_AuthRefresh" },
            })

            const retryCount = parseInt(sessionStorage.getItem("retryCount") ?? "0", 10)
            if (retryCount >= MAX_RETRIES) {
                this.setState({ isRefreshing: false })
                appInsights.trackEvent({ name: "ErrorBoundary_MaxRetriesReached" })
            }
        }
    }

    render() {
        const retryCount = parseInt(sessionStorage.getItem("retryCount") ?? "0", 10)
        if (this.state.hasError && !this.state.isRecoverableEffectCleanupError) {
            return retryCount < MAX_RETRIES ? <FallbackErrorComponent /> : <FallbackErrorComponent />
        }

        return this.props.children
    }
}
